import React from "react";

interface Props {
  children: React.ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * App-level error boundary. Without one, any uncaught render/lifecycle error
 * unmounts the whole React tree and the extension side panel goes blank and
 * "stops working until reload" — the reported intermittent auto-match symptom.
 * This catches such errors and shows a recoverable fallback (Try again / Reload)
 * so a transient failure in one render doesn't wedge the entire panel.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface in the console for field diagnostics; no PHI is included here.
    console.error("[ErrorBoundary] render error:", error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        className="max-w-md w-full mx-auto px-6 py-16 flex flex-col items-center gap-4 text-center"
      >
        <h1 className="text-lg font-semibold text-foreground m-0">
          Something went wrong
        </h1>
        <p className="text-sm text-muted-foreground m-0">
          The panel hit an unexpected error. You can try again without losing
          your session, or reload if it persists.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={this.handleReset}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
